import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { useCollections } from '../hooks/useCollections';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import type { ManageableCatalogInfo } from '../contexts/ActiveCatalogContext';
import { CollectionCreate } from '../types/collection';

interface CreateCollectionModalProps {
  open: boolean;
  onClose: () => void;
}

/** Sentinel MUI value for the caller's own catalog (own pool has a null owner id). */
const OWN_POOL_KEY = '__own__';

/** Stable dropdown key/value for a pool (own collapses to one entry). */
const poolKey = (c: ManageableCatalogInfo) => c.ownerUserId ?? OWN_POOL_KEY;

/**
 * Fallback own pool used only when `manageableCatalogs` hasn't populated yet
 * (the owned-catalogs fetch is in flight). The page gate lets an owner open the
 * dialog synchronously, so this guarantees they can still target their own
 * catalog during that window. A pure delegate can't reach an empty list — the
 * gate requires a manageable catalog for them.
 */
const SYNTH_OWN_POOL: ManageableCatalogInfo = {
  catalogAliId: null,
  label: 'My Catalog',
  ownerUserId: null,
  kind: 'own',
};

export const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { createCollection, isLoading } = useCollections();
  const { manageableCatalogs = [], activeCatalogOwnerId, switchToCatalogAliId } = useActiveCatalog();
  const [formData, setFormData] = useState<CollectionCreate>({
    name: '',
    description: '',
    visibility: 'private'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The pools the dialog offers come straight from the context's shared
  // `manageableCatalogs` (own catalog + active full delegations, deduped by
  // owner) — the SAME source the Albums page gate uses, so the dialog can never
  // offer a catalog the gate would have hidden. Fall back to the own pool only
  // while that list is still loading (see SYNTH_OWN_POOL).
  const pools = manageableCatalogs.length > 0 ? manageableCatalogs : [SYNTH_OWN_POOL];

  // The pool matching the globally active catalog — the default selection. When
  // the active catalog is one the user can't manage (e.g. a view-only delegated
  // catalog they're browsing), there is no match and we default to the first
  // pool (their own catalog).
  const activeIsManageable = pools.some((c) => c.ownerUserId === (activeCatalogOwnerId ?? null));
  const activePoolKey = poolKey(
    pools.find((c) => c.ownerUserId === (activeCatalogOwnerId ?? null)) ?? pools[0],
  );

  // Store only the user's explicit override (null = "follow the active
  // catalog"). The effective selection falls back to the active pool, so the
  // dropdown always defaults to wherever the user currently is — without a
  // sync effect. handleClose resets the override so reopening re-defaults.
  const [selectedPoolOverride, setSelectedPoolOverride] = useState<string | null>(null);
  const selectedPoolKey = selectedPoolOverride ?? activePoolKey;

  // Show the picker when there's a real choice (>1 pool) OR when the active
  // catalog isn't manageable — so the user always sees, explicitly, that the
  // album will land in a different catalog than the one they're viewing rather
  // than being silently retargeted + switched on submit.
  const showCatalogSelect = pools.length > 1 || !activeIsManageable;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Resolve the chosen pool. If it vanished while the dialog was open (the
    // delegation expired or was revoked, so manageableCatalogs recomputed
    // without it), surface an error instead of silently creating in the wrong
    // catalog (e.g. falling back to the user's own).
    const target = pools.find((c) => poolKey(c) === selectedPoolKey);
    if (!target) {
      setErrors({ submit: 'That catalog is no longer available. Please choose another.' });
      return;
    }

    try {
      // The own pool collapses all of the user's own catalogs (catalogAliId
      // null), so for it pass `undefined` → createCollection scopes to the
      // ACTIVE catalog ALI (whichever own catalog is being viewed). A delegated
      // pool carries the delegator's catalog ALI explicitly.
      await createCollection(formData, target.ownerUserId, target.catalogAliId ?? undefined);
      // When the album was created in a different pool than the active catalog,
      // follow it so the user lands where the new album lives (and the list
      // refetches for that catalog). Same-pool creates need no switch.
      if (target.ownerUserId !== (activeCatalogOwnerId ?? null)) {
        switchToCatalogAliId(target.catalogAliId);
      }
      handleClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setErrors({ submit: error.response?.data?.message || error.message || 'Failed to create collection' });
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', visibility: 'private' });
    setErrors({});
    setSelectedPoolOverride(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
      <DialogTitle>
        Create New Album
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {showCatalogSelect && (
          <TextField
            select
            label="Catalog"
            value={selectedPoolKey}
            onChange={(e) => setSelectedPoolOverride(e.target.value)}
            fullWidth
            helperText="Choose which catalog this album belongs to"
            sx={{ mb: 2, mt: 1 }}
          >
            {pools.map((c) => (
              <MenuItem key={poolKey(c)} value={poolKey(c)}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label="Album Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          fullWidth
          required
          error={!!errors.name}
          helperText={errors.name}
          sx={{ mb: 2, mt: showCatalogSelect ? 0 : 1 }}
        />

        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          fullWidth
          multiline
          rows={3}
          error={!!errors.description}
          helperText={errors.description}
          sx={{ mb: 2 }}
        />

        <TextField
          select
          label="Visibility"
          value={formData.visibility}
          onChange={(e) => setFormData({ ...formData, visibility: e.target.value as 'private' | 'public' })}
          fullWidth
        >
          <MenuItem value="private">Private</MenuItem>
          <MenuItem value="public">Public</MenuItem>
        </TextField>

        {errors.submit && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errors.submit}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Album'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
